import { TooltipBox } from '@loomhq/lens';
import React, { useCallback, useEffect, useState } from 'react';
import { useLayer } from 'react-laag';

import Waveform from './waveform';
import { useClipsState } from './clips/state';
import { Clip } from '../../../../../shared/types/clips';

interface Props {
  clip: Clip;
  idx: number;
  currentTime: number;
  onClick: (
    event: React.MouseEvent,
    { secs, clipIdx }: { secs: number; clipIdx: number }
  ) => void;
  recentlyAddedInfo: JSX.Element | false;
}

const ClipEditor = ({
  clip,
  idx,
  currentTime,
  onClick,
  recentlyAddedInfo,
}: Props): JSX.Element => {
  const { waveform_url: waveformUrl } = clip;

  const [waveformData, setWaveformData] = useState([]);

  const clipStateCallback = useCallback(
    state => ({
      focusedClipIndex: state.focusedClipIndex,
      focusedPlayRangeIndex: state.focusedPlayRangeIndex,
      updatePlayRange: state.updatePlayRangeForClip(idx),
    }),
    [idx]
  );

  const { focusedClipIndex, focusedPlayRangeIndex, updatePlayRange } =
    useClipsState(clipStateCallback);

  const getWaveformJSON = useCallback(async () => {
    // @ts-expect-error ignore due to enabling strict null checks
    const resp = await fetch(waveformUrl);

    if (!resp.ok) {
      throw new Error('Failed to fetch waveform json');
    }

    const data = await resp.json();

    setWaveformData(data);
  }, [waveformUrl]);

  useEffect(() => {
    if (waveformUrl) {
      getWaveformJSON();
    }
  }, [waveformUrl, getWaveformJSON]);

  const { layerProps, triggerProps, renderLayer } = useLayer({
    isOpen: Boolean(recentlyAddedInfo),
    placement: 'top-center',
    possiblePlacements: ['top-start', 'top-center', 'top-end'],
    triggerOffset: -10,
    auto: true,
    container: 'edit-tools',
    ResizeObserver,
  });

  return (
    <>
      {recentlyAddedInfo &&
        renderLayer(
          <div {...layerProps} style={{ ...layerProps.style }}>
            <TooltipBox>{recentlyAddedInfo}</TooltipBox>
          </div>
        )}
      <div {...triggerProps}>
        <Waveform
          key={clip.id}
          clipId={clip.id}
          viewboxHeight={80}
          peaks={waveformData}
          peakCount={1000}
          isFocusedClip={idx === focusedClipIndex}
          cursorWidth={2}
          cursorVerticalMargin={16}
          cursorPos={currentTime}
          duration={clip.duration || 1}
          normalize={false}
          sectionOutlineWidth={2}
          onClick={onClick}
          // @ts-expect-error ignore due to enabling strict null checks
          playRanges={clip.playRanges}
          selectedPlayRange={focusedPlayRangeIndex}
          updatePlayRange={updatePlayRange}
          waveformStatus={clip.waveform_generation}
          // @ts-expect-error ignore due to enabling strict null checks
          hasAudio={clip.video_properties.microphone_enabled}
        />
      </div>
    </>
  );
};

export default ClipEditor;
